import React, { useState, useEffect } from 'react';
import { Search, ArrowRight, Book } from 'react-feather';
import * as Icons from 'react-feather';
import AgentLayout from '../AgentLayout';
import { supportCategories, searchArticles } from './supportData';
import '../AgentDashboard.css';

const SupportHub = ({
  agentInfo,
  onDashboard,
  onMyListings,
  onAddProperty,
  onLogout,
  onInvitations,
  onShowings,
  onConversations,
  onAvailableLeads,
  onTeamRoster,
  onSupportHub,
  onNavigateToCategory,
  onNavigateToArticle
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLightMode, setIsLightMode] = useState(true);

  useEffect(() => {
    const saved = localStorage.getItem('agent-theme');
    setIsLightMode(saved !== 'dark');
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim()) {
        const results = searchArticles(searchQuery);
        setSearchResults(results);
      } else {
        setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleArticleClick = (categorySlug, articleSlug) => {
    if (onNavigateToArticle) {
      onNavigateToArticle(categorySlug, articleSlug);
    }
  };

  const getIcon = (iconName, size = 24) => {
    const IconComponent = Icons[iconName];
    return IconComponent ? <IconComponent size={size} /> : <Book size={size} />;
  };

  // Theme colors
  const colors = {
    bg: isLightMode ? '#ffffff' : '#0a0a0a',
    text: isLightMode ? '#1a1a1a' : '#ffffff',
    textMuted: isLightMode ? 'rgba(26, 26, 26, 0.6)' : 'rgba(255, 255, 255, 0.6)',
    textFaint: isLightMode ? 'rgba(26, 26, 26, 0.4)' : 'rgba(255, 255, 255, 0.4)',
    border: isLightMode ? 'rgba(26, 26, 26, 0.08)' : 'rgba(255, 255, 255, 0.08)',
    cardBg: isLightMode ? '#fafafa' : 'rgba(255, 255, 255, 0.03)',
    cardHoverBg: isLightMode ? '#f5f5f5' : 'rgba(255, 255, 255, 0.06)',
    inputBg: isLightMode ? '#f5f5f5' : 'rgba(255, 255, 255, 0.06)',
    searchDropdownBg: isLightMode ? '#ffffff' : 'rgba(26, 26, 26, 0.98)'
  };

  const hubContent = (
    <div style={{
      backgroundColor: colors.bg,
      minHeight: '100vh',
      padding: '60px 40px'
    }}>
      <style>{`
        .light-mode .support-hub-title,
        .dashboard-container.light-mode .support-hub-title {
          color: #1a1a1a !important;
        }
        .light-mode .support-hub-subtitle,
        .dashboard-container.light-mode .support-hub-subtitle {
          color: rgba(26, 26, 26, 0.6) !important;
        }
      `}</style>
      
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <h1 
            className="support-hub-title"
            style={{ 
              fontSize: '2.75rem', 
              fontWeight: '700', 
              color: colors.text, 
              marginBottom: '16px',
              letterSpacing: '-0.5px',
              lineHeight: '1.2'
            }}
          >
            Lauft Agent Portal
            <br />
            <span style={{ fontWeight: '400', fontStyle: 'italic' }}>Documentation</span>
          </h1>
          <p 
            className="support-hub-subtitle"
            style={{ 
              fontSize: '1.1rem', 
              color: colors.textMuted,
              maxWidth: '500px',
              margin: '0 auto 32px'
            }}
          >
            Everything you need to manage buyers and grow your business.
          </p>

          {/* Search */}
          <div style={{ position: 'relative', maxWidth: '500px', margin: '0 auto' }}>
            <Search 
              size={18} 
              style={{ 
                position: 'absolute', 
                left: '16px', 
                top: '50%', 
                transform: 'translateY(-50%)', 
                color: colors.textFaint 
              }} 
            />
            <input
              type="text"
              placeholder="Find anything: showings, leads, listings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '14px 16px 14px 48px',
                fontSize: '0.95rem',
                backgroundColor: colors.inputBg,
                border: `1px solid ${colors.border}`,
                borderRadius: '10px',
                color: colors.text,
                outline: 'none'
              }}
            />

            {/* Search Results Dropdown */}
            {searchQuery.trim() && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                marginTop: '8px',
                backgroundColor: colors.searchDropdownBg,
                border: `1px solid ${colors.border}`,
                borderRadius: '10px',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
                zIndex: 100,
                maxHeight: '300px',
                overflowY: 'auto'
              }}>
                {searchResults.length > 0 ? (
                  searchResults.map((result) => (
                    <div
                      key={`${result.categorySlug}-${result.slug}`}
                      onClick={() => handleArticleClick(result.categorySlug, result.slug)}
                      style={{ 
                        padding: '12px 16px', 
                        cursor: 'pointer',
                        borderBottom: `1px solid ${colors.border}`
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.cardHoverBg}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <div style={{ fontSize: '0.9rem', fontWeight: '500', color: colors.text }}>
                        {result.title}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: colors.textMuted, marginTop: '2px' }}>
                        {result.categoryTitle}
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ padding: '16px', textAlign: 'center', color: colors.textMuted }}>
                    No results found
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Categories with Articles */}
        {supportCategories.map((category) => (
          <div key={category.slug} style={{ marginBottom: '48px' }}>
            {/* Category Header */}
            <h2 style={{ 
              fontSize: '1.5rem', 
              fontWeight: '600', 
              color: colors.text, 
              marginBottom: '20px'
            }}>
              {category.title}
            </h2>

            {/* Articles Grid */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', 
              gap: '16px' 
            }}>
              {category.articles.map((article) => (
                <div
                  key={article.slug}
                  onClick={() => handleArticleClick(category.slug, article.slug)}
                  style={{
                    backgroundColor: colors.cardBg,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '12px',
                    padding: '24px',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = colors.cardHoverBg;
                    e.currentTarget.style.borderColor = isLightMode ? 'rgba(26, 26, 26, 0.15)' : 'rgba(255, 255, 255, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = colors.cardBg;
                    e.currentTarget.style.borderColor = colors.border;
                  }}
                >
                  {/* Icon placeholder area */}
                  <div style={{
                    width: '100%',
                    height: '80px',
                    backgroundColor: isLightMode ? '#f0f0f0' : 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: colors.textFaint
                  }}>
                    {getIcon(category.icon, 32)}
                  </div>
                  
                  <h3 style={{ 
                    fontSize: '1rem', 
                    fontWeight: '600', 
                    color: colors.text, 
                    marginBottom: '8px',
                    lineHeight: '1.3'
                  }}>
                    {article.title}
                  </h3>
                  <p style={{ 
                    fontSize: '0.85rem', 
                    color: colors.textMuted, 
                    lineHeight: '1.5',
                    margin: 0
                  }}>
                    {article.summary}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <AgentLayout
      activeTab="support"
      onDashboard={onDashboard}
      onMyListings={onMyListings}
      onAddProperty={onAddProperty}
      onInvitations={onInvitations}
      onShowings={onShowings}
      onConversations={onConversations}
      onAvailableLeads={onAvailableLeads}
      onTeamRoster={onTeamRoster}
      onSupportHub={onSupportHub}
      onLogout={onLogout}
    >
      {hubContent}
    </AgentLayout>
  );
};

export default SupportHub;